import React, { useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, Loader2, Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 ptg-grid-bg p-4" data-testid="forgot-password-page">
      <Seo title="Forgot Password" />
      <div className="w-full max-w-md rounded-3xl bg-white p-8 sm:p-10 shadow-2xl">
        <div className="flex items-center gap-3 mb-8">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-900 text-white"><GraduationCap className="h-7 w-7" /></div>
          <div>
            <div className="font-heading text-xl font-extrabold text-slate-900">PTG Admin</div>
            <div className="text-xs font-semibold text-sky-700 uppercase tracking-widest">Password Reset</div>
          </div>
        </div>
        {sent ? (
          <div className="text-center" data-testid="forgot-password-success">
            <CheckCircle2 className="h-14 w-14 text-green-500 mx-auto" />
            <h1 className="mt-4 text-xl font-bold text-slate-900">Check your email</h1>
            <p className="mt-2 text-sm text-slate-600">If an account exists for <span className="font-semibold">{email}</span>, we've sent a password reset link. It is valid for 1 hour.</p>
            <Link to="/admin/login" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-900 hover:underline" data-testid="back-to-login-link"><ArrowLeft className="h-4 w-4" /> Back to login</Link>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Mail className="h-5 w-5 text-blue-900" /> Forgot password</h1>
            <p className="text-sm text-slate-500 mt-1">Enter your admin email and we'll send you a reset link.</p>
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="forgot-password-form">
              <div>
                <Label className="mb-1.5 block font-semibold text-slate-700">Email</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin email" data-testid="forgot-email" required />
              </div>
              <Button type="submit" disabled={loading} className="w-full h-11 bg-blue-900 hover:bg-blue-950" data-testid="forgot-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
              </Button>
            </form>
            <Link to="/admin/login" className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-blue-900" data-testid="back-to-login-link"><ArrowLeft className="h-4 w-4" /> Back to login</Link>
          </>
        )}
      </div>
    </div>
  );
}
